// src/pages/client/TermsPage.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  ArrowLeft,
  FileText,
  Shield,
  CheckCircle,
  AlertCircle,
  Clock,
  Heart,
  Users,
  Lock,
  Eye,
  Database,
  Cookie,
  Mail,
  Phone,
  MapPin,
  Globe,
  Award,
  ThumbsUp,
  Loader
} from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext'; 
import useTranslation from '../../hooks/useTranslation';
import colors from '../../constants/colors';
import styles from '../../styles/TermsPage.module.css';
import {
  getTermsOfService,
  acceptTermsAndPrivacy,
  checkUserAcceptance,
  selectTermsOfService,
  selectUserAcceptance,
  selectSettingsLoading,
  selectSettingsProcessing,
  selectSettingsError,
  selectSettingsProcessSuccess,
  clearSettingsErrors,
  clearSettingsSuccess
} from '../../store/slices/platformSettingsSlice';

const TermsPage = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { userId } = useParams();
  
  // ======================================================
  // LOCAL STATE
  // ======================================================
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [hasAttemptedAccept, setHasAttemptedAccept] = useState(false);

  // ======================================================
  // CONTEXT & HOOKS
  // ======================================================
  const { currentLanguage } = useLanguage();

  // ======================================================
  // REDUX STATE
  // ======================================================
  const termsOfService = useSelector(selectTermsOfService);
  const userAcceptance = useSelector(selectUserAcceptance);
  const loading = useSelector(selectSettingsLoading);
  const processing = useSelector(selectSettingsProcessing);
  const error = useSelector(selectSettingsError);
  const processSuccess = useSelector(selectSettingsProcessSuccess);

  // ======================================================
  // TRANSLATIONS
  // ======================================================
  const { translatedText: termsOfServiceTitle } = useTranslation('Terms of Service');
  const { translatedText: lastUpdated } = useTranslation('Last Updated');
  const { translatedText: pleaseAcceptTerms } = useTranslation('Please accept the Terms of Service before continuing.');
  const { translatedText: acceptedSuccess } = useTranslation('Accepted Successfully');
  const { translatedText: thankYouAccept } = useTranslation('Thank you for accepting our terms of service.');
  const { translatedText: loadingText } = useTranslation('Loading...');
  const { translatedText: errorOccurred } = useTranslation('An error occurred');
  const { translatedText: tryAgain } = useTranslation('Try Again');
  const { translatedText: alreadyAccepted } = useTranslation('You have already accepted the latest terms of service');
  const { translatedText: acceptTermsButton } = useTranslation('Accept Terms of Service');
  const { translatedText: iHaveReadTerms } = useTranslation('I have read and agree to the Terms of Service');
  const { translatedText: version } = useTranslation('Version');
  const { translatedText: noContent } = useTranslation('No content available');

  // ======================================================
  // FOOTER TRANSLATIONS
  // ======================================================
  const { translatedText: homeTitle } = useTranslation('Haitian Services Platform');
  const { translatedText: connectingHaitians } = useTranslation('Connecting Haitians Worldwide, One Service at a Time');
  const { translatedText: madeWithLove } = useTranslation('All rights reserved');
  const { translatedText: quickLinks } = useTranslation('Quick Links');
  const { translatedText: findProviders } = useTranslation('Find Providers');
  const { translatedText: supportUs } = useTranslation('Support Us');
  const { translatedText: helpAndSupport } = useTranslation('Help & Support');
  const { translatedText: aboutUs } = useTranslation('About Us');
  const { translatedText: forProviders } = useTranslation('For Providers');
  const { translatedText: becomeAProvider } = useTranslation('Become a Provider');
  const { translatedText: providerLogin } = useTranslation('Provider Login');
  const { translatedText: dashboard } = useTranslation('Dashboard');
  const { translatedText: settings } = useTranslation('Settings');
  const { translatedText: legal } = useTranslation('Legal');
  const { translatedText: termsOfServiceLink } = useTranslation('Terms of Service');
  const { translatedText: privacyPolicy } = useTranslation('Privacy Policy');
  const { translatedText: cookiePolicy } = useTranslation('Cookie Policy');

  // ======================================================
  // EFFECTS
  // ======================================================
  
  // Load terms of service on mount
  useEffect(() => {
    loadTermsOfService();
  }, [currentLanguage]);

  // Check user acceptance status
  useEffect(() => {
    if (userId) {
      dispatch(checkUserAcceptance(userId));
    }
  }, [userId, dispatch]);

  // Handle successful acceptance
  useEffect(() => {
    if (processSuccess && hasAttemptedAccept) {
      alert(`${acceptedSuccess}\n\n${thankYouAccept}`);
      dispatch(clearSettingsSuccess());
      setHasAttemptedAccept(false);
      setAcceptedTerms(false);
      
      // Refresh acceptance status
      if (userId) {
        dispatch(checkUserAcceptance(userId));
      }
      
      // Navigate back if callback exists
      navigate(-1);
    }
  }, [processSuccess, hasAttemptedAccept]);

  // Handle errors
  useEffect(() => {
    if (error) {
      alert(`${errorOccurred}\n\n${error}`);
      dispatch(clearSettingsErrors());
    }
  }, [error]);

  // ======================================================
  // FUNCTIONS
  // ======================================================
  
  const loadTermsOfService = () => {
    dispatch(getTermsOfService(currentLanguage));
  };

  const handleAcceptToggle = () => {
    setAcceptedTerms(!acceptedTerms);
  };

  const handleAccept = () => {
    if (!acceptedTerms) {
      alert(pleaseAcceptTerms);
      return;
    }

    if (!userId) {
      alert('User not authenticated. Please log in to accept the terms of service.');
      return;
    }

    // Check if already accepted
    if (userAcceptance?.hasAccepted) {
      alert(alreadyAccepted);
      return;
    }

    const termsVersion = termsOfService?.version || '1.0.0';

    setHasAttemptedAccept(true);
    dispatch(acceptTermsAndPrivacy({
      userId,
      termsVersion,
      privacyVersion: '1.0.0' // Use actual privacy version if available
    }));
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // ======================================================
  // RENDER FUNCTIONS
  // ======================================================
  
  const renderContent = () => {
    if (loading && !termsOfService) {
      return (
        <div className={styles.loadingContainer}>
          <Loader size={48} className={styles.spinner} />
          <p>{loadingText}</p>
        </div>
      );
    }

    if (!termsOfService || !termsOfService.content) {
      return (
        <div className={styles.emptyContainer}>
          <FileText size={60} color={colors.gray} />
          <p>{noContent}</p>
          <button className={styles.retryButton} onClick={loadTermsOfService}>
            {tryAgain}
          </button>
        </div>
      );
    }

    const sections = termsOfService.content.sections || [];

    return (
      <>
        {/* Header Info */}
        <div className={styles.infoSection}>
          <h5 className={styles.infoTitle}>{termsOfService.title}</h5>
          <p className={styles.infoDate}>
            {lastUpdated}: {formatDate(termsOfService.lastUpdated)}
          </p>
          <p className={styles.infoVersion}>
            {version}: {termsOfService.version}
          </p>
          <div className={styles.infoBadges}>
            <span className={styles.infoBadge}>
              <Shield size={14} />
              {termsOfService.isActive ? ' Active' : ' Inactive'}
            </span>
            {termsOfService.requiresAcceptance && (
              <span className={styles.infoBadge}>
                <CheckCircle size={14} />
                Acceptance Required
              </span>
            )}
            {termsOfService.acceptanceRequiredFor && (
              <span className={styles.infoBadge}>
                <Users size={14} />
                For: {termsOfService.acceptanceRequiredFor.replace('_', ' ')}
              </span>
            )}
          </div>
        </div>

        {/* Sections - Dynamic from API */}
        {sections.map((section) => (
          <div key={section.order || section._id} className={styles.sectionContainer}>
            <div className={styles.sectionHeader}>
              <FileText size={20} color={colors.primary} />
              <h6 className={styles.sectionTitle}>{section.title}</h6>
            </div>
            <p className={styles.sectionContent}>{section.content}</p>
          </div>
        ))}

        {/* Acceptance Section - Only show if user is logged in */}
        {userId && (
          <div className={styles.acceptanceContainer}>
            <div className={styles.acceptanceRow}>
              <label className={styles.acceptanceLabel}>
                <input
                  type="checkbox"
                  checked={acceptedTerms}
                  onChange={handleAcceptToggle}
                  disabled={processing || userAcceptance?.hasAccepted}
                  className={styles.acceptanceCheckbox}
                />
                <span className={styles.acceptanceText}>{iHaveReadTerms}</span>
              </label>
            </div>

            {userAcceptance?.hasAccepted ? (
              <div className={styles.acceptedBadge}>
                <CheckCircle size={20} color={colors.success} />
                <span className={styles.acceptedBadgeText}>
                  Accepted on {formatDate(userAcceptance.acceptedAt)}
                </span>
                {userAcceptance.termsVersion && (
                  <span className={styles.acceptedVersion}>
                    Version: {userAcceptance.termsVersion}
                  </span>
                )}
              </div>
            ) : (
              <button
                className={`${styles.acceptButton} ${(!acceptedTerms || processing) ? styles.acceptButtonDisabled : ''}`}
                onClick={handleAccept}
                disabled={!acceptedTerms || processing}
              >
                {processing ? (
                  <>
                    <Loader size={20} className={styles.spinner} />
                    Processing...
                  </>
                ) : (
                  acceptTermsButton
                )}
              </button>
            )}

            {userAcceptance && !userAcceptance.hasAccepted && userAcceptance.requireReAcceptance && (
              <p className={styles.reAcceptanceText}>
                ⚠️ Please accept the latest version of our Terms of Service
              </p>
            )}
          </div>
        )}
      </>
    );
  };

  // ======================================================
  // RENDER
  // ======================================================
  
  return (
    <div className={styles.termsContainer}>
      {/* Header */}
      <div className={styles.termsHeader}>
        <div className={styles.headerContent}>
          <button 
            className={styles.backButton}
            onClick={() => navigate(-1)}
          >
            <ArrowLeft size={24} color={colors.white} />
          </button>
          <span className={styles.headerTitle}>{termsOfServiceTitle}</span>
        </div>
      </div>

      {/* Content */}
      <div className={styles.scrollContent}>
        <div className={styles.container}>
          {renderContent()}
        </div>
      </div>

      {/* Footer - FULLY TRANSLATED */}
      <footer className={styles.footerFull}>
        <div className={styles.footerContent}>
          <div className={styles.footerGridFull}>
            {/* Brand Column */}
            <div className={styles.footerBrandColumn}>
              <div className={styles.footerBrandContainer}>
                <div className={styles.footerLogoWrapper}>
                  <img 
                    src="/logo/logo_hsm.png" 
                    alt="Haitian Services"
                    className={styles.footerLogo}
                  />
                </div>
                <div>
                  <div className={styles.footerBrandName}>{homeTitle}</div>
                  <div className={styles.footerBrandDescription}>{connectingHaitians}</div>
                </div>
              </div>
              <div className={styles.footerSocialLinks}>
                <a href="#" target="_blank" rel="noopener noreferrer" className={styles.footerSocialIcon}>
                  <img src="https://s.magecdn.com/social/tc-facebook.svg" alt="Facebook" width="20" height="20" />
                </a>
                <a href="#" target="_blank" rel="noopener noreferrer" className={styles.footerSocialIcon}>
                  <img src="https://s.magecdn.com/social/tc-instagram.svg" alt="Instagram" width="20" height="20" />
                </a>
                <a href="#" target="_blank" rel="noopener noreferrer" className={styles.footerSocialIcon}>
                  <img src="https://s.magecdn.com/social/tc-twitter.svg" alt="Twitter" width="20" height="20" />
                </a>
                <a href="#" target="_blank" rel="noopener noreferrer" className={styles.footerSocialIcon}>
                  <img src="https://s.magecdn.com/social/tc-youtube.svg" alt="YouTube" width="20" height="20" />
                </a>
              </div>
            </div>

            {/* Quick Links - TRANSLATED */}
            <div className={styles.footerLinksColumn}>
              <h3 className={styles.footerHeading}>{quickLinks}</h3>
              <ul className={styles.footerLinks}>
                <li><a href="/providers">{findProviders}</a></li>
                <li><a href="/donation">{supportUs}</a></li>
                <li><a href="/help-support">{helpAndSupport}</a></li>
                <li><a href="/about">{aboutUs}</a></li>
              </ul>
            </div>

            {/* For Providers - TRANSLATED */}
            <div className={styles.footerLinksColumn}>
              <h3 className={styles.footerHeading}>{forProviders}</h3>
              <ul className={styles.footerLinks}>
                <li><a href="/register">{becomeAProvider}</a></li>
                <li><a href="/login">{providerLogin}</a></li>
                <li><a href="/provider-dashboard">{dashboard}</a></li>
                <li><a href="/provider-settings">{settings}</a></li>
              </ul>
            </div>

            {/* Legal - TRANSLATED */}
            <div className={styles.footerLinksColumn}>
              <h3 className={styles.footerHeading}>{legal}</h3>
              <ul className={styles.footerLinks}>
                <li><a href="/terms">{termsOfServiceLink}</a></li>
                <li><a href="/privacy">{privacyPolicy}</a></li>
                <li><a href="/cookies">{cookiePolicy}</a></li>
              </ul>
            </div>
          </div>

          {/* Footer Bottom */}
          <div className={styles.footerDividerFull}></div>
          <div className={styles.footerBottomFull}>
            <div className={styles.footerFlag}>
              <div className={styles.flagStripes}>
                <div className={`${styles.stripe} ${styles.stripeBlue}`}></div>
                <div className={`${styles.stripe} ${styles.stripeRed}`}></div>
              </div>
            </div>
            <div className={styles.footerBottomText}>
              © 2026 {homeTitle}, {madeWithLove}
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default TermsPage;